import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { StorageService } from './storage.service';
import { LoginRequest, LoginResponse, DecodedToken, User } from '../models/user.model';
import { Role, ROLE_DASHBOARD_ROUTES } from '../enums/role.enum';
import { SKIP_AUTH } from '../interceptors/auth-context';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}/auth`;

  private currentUser = signal<User | null>(null);
  private loggingOut = false;

  readonly user = this.currentUser.asReadonly();
  readonly isLoggedIn = computed(() => !!this.currentUser());
  readonly userRole = computed(() => this.currentUser()?.role as Role | undefined);

  constructor(
    private http: HttpClient,
    private router: Router,
    private storage: StorageService,
  ) {
    this.loadUserFromStorage();
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/login`, credentials).pipe(
      tap((response) => {
        this.storage.setToken(response.accessToken || response.access_token);
        this.storage.setUser(response.user);
        this.currentUser.set(response.user);
      }),
    );
  }

  logout(): void {
    if (this.loggingOut) return;
    this.loggingOut = true;
    this.http
      .post(`${this.apiUrl}/logout`, {}, { context: new HttpContext().set(SKIP_AUTH, true) })
      .subscribe({ error: () => {} });
    this.storage.clear();
    this.currentUser.set(null);
    this.router.navigate(['/auth/login']).then(() => { this.loggingOut = false; });
  }

  getToken(): string | null {
    return this.storage.getToken();
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const decoded = this.decodeToken(token);
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  redirectToDashboard(): void {
    const role = this.userRole();
    if (role && ROLE_DASHBOARD_ROUTES[role]) {
      this.router.navigate([ROLE_DASHBOARD_ROUTES[role]]);
    } else {
      this.router.navigate(['/auth/login']);
    }
  }

  private loadUserFromStorage(): void {
    const user = this.storage.getUser();
    if (user && !this.isTokenExpired()) {
      this.currentUser.set(user);
    } else if (user) {
      this.storage.clear();
    }
  }

  private decodeToken(token: string): DecodedToken {
    const payload = token.split('.')[1];
    const decoded = atob(payload);
    return JSON.parse(decoded);
  }
}
